import { Dispatch, SetStateAction } from "react";
import { Icon, ToggleButton } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import AiSparkWithGradientIcon from "@/icons/AiSparkWithGradientIcon/AiSparkWithGradientIcon";

import {
  innerContentCss,
  innerBorderCss,
  rootContainerCss,
  svgCss,
  textAreaRootContainerCss,
  labelTextCss,
  textAreaCss,
  headingSectionExpandedCss,
  toggleSwitchContainerCss,
} from "./AIExpandableTextArea.styles";

interface AIExpandableTextAreaProps {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  handleExpandAndFocus: () => void;
  handleContainerBlur: (e: React.FocusEvent) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  isMobileDisplay?: boolean;
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
}

const ToggleWithTrackEvent = withTrackEvent(ToggleButton);

const textAreaDescriptor = "search-with-luna-textarea";

const AIExpandableTextArea = ({
  containerRef,
  expanded,
  handleExpandAndFocus,
  handleContainerBlur,
  isMobileDisplay,
  inputValue,
  setExpanded,
  setInputValue,
  textAreaRef,
}: AIExpandableTextAreaProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      setExpanded(false);
    }

    // If Shift+Enter -> then no preventDefault, so user can create a newline
    if (e.key === "Enter" && !e.shiftKey) {
      // Prevents textarea's native newline functionality so it can
      // submit the query instead.
      e.preventDefault();

      // TODO: Handle searching query here.

      // Collapse after submit
      setExpanded(false);
    }
  };

  const sparkleIcon = (
    <Icon
      SvgComponent={AiSparkWithGradientIcon}
      size={Icon.sizes.sm}
      isDecorative
      __cssFor={{ root: svgCss }}
    />
  );

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      aria-expanded={expanded}
      aria-label="Search with Luna input"
      onFocus={handleExpandAndFocus}
      onBlur={handleContainerBlur}
      css={rootContainerCss(expanded, isMobileDisplay)}
      data-qa-selector="search-with-luna-toggle"
    >
      <div css={innerBorderCss(expanded, isMobileDisplay)}>
        <div css={innerContentCss(expanded)}>
          {expanded ? (
            <div css={headingSectionExpandedCss}>
              {sparkleIcon}
              <span>What are you looking for?</span>
            </div>
          ) : (
            sparkleIcon
          )}
          <div css={textAreaRootContainerCss(expanded)}>
            <label htmlFor={textAreaDescriptor} css={labelTextCss}>
              Search with Luna
            </label>
            <textarea
              css={textAreaCss(expanded)}
              ref={textAreaRef}
              id={textAreaDescriptor}
              value={inputValue}
              placeholder={
                expanded
                  ? "Find me a 7th grade article on the importance of recycling that connects to RI.7.5"
                  : "What are you looking for?"
              }
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              data-qa-selector={textAreaDescriptor}
              name="needle"
              rows={expanded ? 7 : 1}
            />
          </div>
          {expanded ? (
            <ToggleWithTrackEvent
              label="Disable Search with Luna"
              __cssFor={{
                switchContainerStyles: toggleSwitchContainerCss,
              }}
              dataProps={{
                "data-qa-selector": "disable-search-with-luna-toggle",
              }}
              objectType={objectTypes.user}
              actionName={actionNames.interact}
              streamProperties={{
                description: "Toggle Search with Luna",
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AIExpandableTextArea;
